import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { DocumentationContent } from '../types/documentation';
import DocumentationHeader from './documentation/DocumentationHeader';
import IntroductionSectionComponent from './documentation/IntroductionSection';
import DescriptionSectionComponent from './documentation/DescriptionSection';
import CodeExamplesSection from './documentation/CodeExamplesSection';
import TopicsSection from './documentation/TopicsSection';
import DownloadSourceFilesSection from './documentation/DownloadSourceFilesSection';
import PropertiesTableSection from './documentation/PropertiesTableSection';

interface DocumentationContentPanelProps {
  content: DocumentationContent;
  drawerOpen: boolean;
}

const DocumentationContentPanel: React.FC<DocumentationContentPanelProps> = ({
  content,
  drawerOpen
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [content]);

  return (
    <Box
      sx={{
        transition: 'margin-left 0.3s ease',
        width: '100%',
        height: 'calc(100vh - 64px)',
        overflow: 'hidden',
        backgroundColor: '#fafafa'
      }}
    >
      <Box 
        ref={scrollContainerRef}
        sx={{ 
          height: '100%',
          overflowY: 'auto',
          padding: 3,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#a8a8a8',
            },
          },
        }}
      >
        <Box sx={{ width: '100%', textAlign: 'left' }}>
          <DocumentationHeader title={content.title} description={content.description} />
          {content.introduction && (
            <IntroductionSectionComponent introduction={content.introduction} />
          )}
          {content.descriptionSection && (
            <DescriptionSectionComponent descriptionSection={content.descriptionSection} />
          )}
          {content.properties && content.properties.length > 0 && (
            <PropertiesTableSection 
              properties={content.properties} 
              title={content.propertiesTitle}
            />
          )}
          {content.codeExamples && content.codeExamples.length > 0 && (
            <CodeExamplesSection codeExamples={content.codeExamples} />
          )}
          {content.downloadSourceFiles && content.downloadSourceFiles.length > 0 && (
            <DownloadSourceFilesSection downloadSourceFiles={content.downloadSourceFiles} />
          )}
          {content.items && content.items.length > 0 && (
            <TopicsSection items={content.items} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DocumentationContentPanel;
